import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { formatInrWhole } from '../utils/money';
import { whatsappUrl } from '../utils/whatsapp';
import './AdminEnquiries.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'responded', label: 'Responded' },
  { value: 'bought', label: 'Bought' },
  { value: 'rejected', label: 'Rejected' },
];

function statusClass(status) {
  const s = (status || 'new').toLowerCase();
  return `enq-badge enq-badge--${s}`;
}

function WhatsAppIcon() {
  return (
    <svg className="enq-wa-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
      />
    </svg>
  );
}

export default function AdminEnquiries() {
  const { admin, loading: authLoading } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  const fetchEnquiries = useCallback(async () => {
    try {
      const q = new URLSearchParams({ page: String(page), limit: String(15) });
      if (statusFilter) q.set('status', statusFilter);
      const res = await fetch(`/api/enquiries?${q.toString()}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setEnquiries(data.enquiries);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const viewDetail = async (id) => {
    try {
      const res = await fetch(`/api/enquiries/${id}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSelectedEnquiry(data);
    } catch (err) {
      console.error(err);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`/api/enquiries/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) return;
      await fetchEnquiries();
      if (selectedEnquiry && selectedEnquiry.id === id) {
        setSelectedEnquiry({ ...selectedEnquiry, status });
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (authLoading) return <div className="page-center"><p>Loading...</p></div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const totalPages = Math.ceil(total / 15);

  const waMessage = (eq) =>
    `Hi ${eq.name}, this is Vikky Gold Palace regarding your enquiry #${eq.id}.`;

  return (
    <div className="admin-enquiries">
      <AdminNav />

      <header className="enq-hero">
        <h1>Customer enquiries</h1>
        <p className="enq-hero-sub">
          Cart checkout requests. Open a row for full line items and to update status.
        </p>
      </header>

      {selectedEnquiry ? (
        <article className="enq-detail">
          <button type="button" className="enq-back" onClick={() => setSelectedEnquiry(null)}>
            ← Back to list
          </button>

          <div className="enq-detail-head">
            <div>
              <span className="enq-detail-id">Enquiry #{selectedEnquiry.id}</span>
              <span className={statusClass(selectedEnquiry.status)}>{selectedEnquiry.status || 'new'}</span>
            </div>
            <time className="enq-detail-time" dateTime={selectedEnquiry.created_at}>
              {new Date(selectedEnquiry.created_at).toLocaleString('en-IN')}
            </time>
          </div>

          <div className="enq-detail-grid">
            <div className="enq-detail-card">
              <h3 className="enq-detail-section-title">Contact</h3>
              <dl className="enq-dl">
                <div><dt>Name</dt><dd>{selectedEnquiry.name}</dd></div>
                <div>
                  <dt>Phone</dt>
                  <dd className="enq-phone-actions">
                    <a href={`tel:${selectedEnquiry.phone}`}>{selectedEnquiry.phone}</a>
                    <a
                      className="enq-wa-btn"
                      href={whatsappUrl(
                        selectedEnquiry.phone,
                        `Hi ${selectedEnquiry.name}, this is Vikky Gold Palace regarding enquiry #${selectedEnquiry.id}.`
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <WhatsAppIcon />
                      <span>WhatsApp</span>
                    </a>
                  </dd>
                </div>
                <div><dt>State</dt><dd>{selectedEnquiry.state || '—'}</dd></div>
                <div><dt>Pincode</dt><dd>{selectedEnquiry.pincode || '—'}</dd></div>
              </dl>
            </div>
            <div className="enq-detail-card enq-detail-card--total">
              <h3 className="enq-detail-section-title">Order value</h3>
              <p className="enq-detail-total-value">
                {formatInrWhole(
                  selectedEnquiry.items.reduce((s, i) => s + parseFloat(i.computed_price || 0), 0)
                )}
              </p>
              <p className="enq-detail-total-note">From snapshot prices at enquiry time</p>
            </div>
          </div>

          <h3 className="enq-items-heading">Line items</h3>
          <div className="enq-items-wrap">
            <table className="enq-items-table">
              <thead>
                <tr>
                  <th className="enq-th-thumb">Photo</th>
                  <th>Product</th>
                  <th>Material</th>
                  <th>Weight</th>
                  <th>Rate/g</th>
                  <th>Qty</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {selectedEnquiry.items.map((item) => (
                  <tr key={item.id}>
                    <td className="enq-item-thumb-cell">
                      {item.jewel_id && item.image_mime_type ? (
                        <img
                          src={`/api/jewels/${item.jewel_id}/image`}
                          alt=""
                          className="enq-item-thumb"
                        />
                      ) : (
                        <span className="enq-item-noimg">—</span>
                      )}
                    </td>
                    <td>
                      <div className="enq-item-product">
                        <div className="enq-item-product-name">{item.snapshot_name}</div>
                        <div className="enq-item-product-meta">
                          <span className="enq-item-product-id">
                            ID: {item.jewel_id || '—'}
                          </span>
                          {item.jewel_id && (
                            <Link
                              to={`/jewel/${item.jewel_id}`}
                              className="enq-item-open-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open product →
                            </Link>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="cap">{item.snapshot_material}</td>
                    <td>{item.snapshot_weight_g ? `${item.snapshot_weight_g}g` : '—'}</td>
                    <td>{item.snapshot_price_per_gram ? formatInrWhole(item.snapshot_price_per_gram) : '—'}</td>
                    <td>{item.quantity}</td>
                    <td className="enq-price-cell">{formatInrWhole(item.computed_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="enq-actions">
            <button type="button" className="enq-btn enq-btn--respond" onClick={() => updateStatus(selectedEnquiry.id, 'responded')}>
              Mark responded
            </button>
            <button type="button" className="enq-btn enq-btn--bought" onClick={() => updateStatus(selectedEnquiry.id, 'bought')}>
              Mark bought
            </button>
            <button type="button" className="enq-btn enq-btn--reject" onClick={() => updateStatus(selectedEnquiry.id, 'rejected')}>
              Reject
            </button>
          </div>
        </article>
      ) : (
        <>
          <div className="enq-toolbar">
            <span className="enq-toolbar-label">Filter</span>
            <div className="enq-filters" role="group" aria-label="Filter by status">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value || 'all'}
                  type="button"
                  className={`enq-filter-chip ${statusFilter === opt.value ? 'is-active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setStatusFilter(opt.value);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="enq-total-count">{total} total</span>
          </div>

          {loading ? (
            <p className="enq-loading">Loading enquiries…</p>
          ) : enquiries.length === 0 ? (
            <div className="enq-empty">
              <p>{statusFilter ? 'No enquiries match this filter.' : 'No enquiries yet. They will appear here when customers submit cart requests.'}</p>
              {statusFilter && (
                <button type="button" className="enq-empty-clear" onClick={() => { setPage(1); setStatusFilter(''); }}>
                  Show all
                </button>
              )}
            </div>
          ) : (
            <>
              <ul className="enq-list">
                {enquiries.map((eq) => (
                  <li key={eq.id} className="enq-row">
                    <div className="enq-row-main">
                      <span className="enq-row-id">#{eq.id}</span>
                      <div className="enq-row-who">
                        <span className="enq-row-name">{eq.name}</span>
                        <a className="enq-row-phone" href={`tel:${eq.phone}`}>{eq.phone}</a>
                      </div>
                      <span className={statusClass(eq.status)}>{eq.status}</span>
                      <span className="enq-row-meta">{eq.item_count} item{eq.item_count !== 1 ? 's' : ''}</span>
                      <span className="enq-row-value">{formatInrWhole(eq.total_value)}</span>
                      <time className="enq-row-date">{new Date(eq.created_at).toLocaleDateString('en-IN')}</time>
                    </div>
                    <div className="enq-row-side">
                      <a
                        className="enq-wa-circle"
                        href={whatsappUrl(eq.phone, waMessage(eq))}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="WhatsApp"
                        aria-label={`WhatsApp ${eq.name}`}
                      >
                        <WhatsAppIcon />
                      </a>
                      <button type="button" className="enq-open" onClick={() => viewDetail(eq.id)}>
                        Open
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              {totalPages > 1 && (
                <div className="enq-pagination">
                  <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </button>
                  <span className="enq-page-info">Page {page} of {totalPages}</span>
                  <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
